import type { ReturnRefundDestination } from "@redotech/customer-portal-sdk/rpc/schema/get-return-refund-destination";
import {
  closedReturnStatuses,
  ReturnStatus,
} from "@redotech/redo-return-core-common/return-status";
import {
  recipientFieldForProvider,
  RefundDestinationProvider,
  RefundRecipientField,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { RefundMethodSummaryProps } from "./refund-method-summary";

export interface ResolveRefundMethodSummaryInput {
  destination: ReturnRefundDestination | undefined;
  returnStatus: ReturnStatus | undefined;
  /** Total heading to the customer, in the return's currency. */
  refundTotal: number;
  /** Rails the team still offers, already filtered for the return's currency. */
  availableProviders: readonly RefundDestinationProvider[];
  onEditClick: () => void;
}

/**
 * Whether to show a refund destination on a submitted return, and whether to
 * offer editing it. Split out from the confirmation page so the gates can be
 * exercised directly — getting one wrong either hides a customer's refund
 * destination or invites them to edit one that will never be paid.
 */
export function resolveRefundMethodSummary({
  destination,
  returnStatus,
  refundTotal,
  availableProviders,
  onEditClick,
}: ResolveRefundMethodSummaryInput): RefundMethodSummaryProps | undefined {
  // Only a refund actually lands in a wallet. A $0 return keeps the rail the
  // customer picked, in case a merchant adjusts it upward later, but there is
  // nothing to show them until then.
  if (!destination || refundTotal <= 0) {
    return undefined;
  }

  const field = recipientFieldForProvider(destination.provider);
  // A closed return will never pay out, and nothing ever cancels its
  // disbursement, so it would otherwise stay editable forever.
  const returnIsClosed =
    !returnStatus || closedReturnStatuses.includes(returnStatus);

  return {
    provider: destination.provider,
    recipient:
      (field === RefundRecipientField.PHONE
        ? destination.recipient.phone
        : destination.recipient.email) ?? "",
    status: destination.status,
    // A single entitled rail is still worth editing: the recipient may be a typo.
    canEdit:
      destination.canEdit && !returnIsClosed && availableProviders.length > 0,
    onEditClick,
  };
}
